import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { ArrowLeft, Loader2, Lock, CheckCircle2, Eye, Paperclip, RefreshCw, Trash2, Users, FileText, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { StatusBadge, GuiaStatusBadge, GuiaViewer } from './GuiaViewer';
import { Badge } from '../ui/badge';
import { UploadGuiaDialog } from './UploadGuiaDialog';
import { errMsg, fmtDate, TIPO_DIVERGENCIA_LABELS, VALIDACAO_STATUS } from './guiasUtils';

const Stat = ({ label, value, cls = '' }) => (
  <div className="rounded-lg border bg-card p-3">
    <p className="text-xs text-muted-foreground">{label}</p>
    <p className={`text-2xl font-bold ${cls}`} data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>{value}</p>
  </div>
);

export const ProfissionalPanel = ({ profKey, competencia, onBack, onChanged }) => {
  const { api, can } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploadPac, setUploadPac] = useState(null);
  const [guiaAlvo, setGuiaAlvo] = useState(null);
  const [viewGuia, setViewGuia] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [removeGuia, setRemoveGuia] = useState(null);
  const [motivo, setMotivo] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/guias/profissionais/${profKey}`, { params: { competencia } });
      setData(r.data);
    } catch (e) { toast.error(errMsg(e, 'Erro ao carregar profissional')); }
    finally { setLoading(false); }
  }, [api, profKey, competencia]);

  useEffect(() => { load(); }, [load]);

  const refresh = () => { load(); onChanged?.(); };

  const confirmarEntrega = async () => {
    setConfirming(true);
    try {
      await api.post(`/guias/profissionais/${profKey}/confirmar-entrega`, { competencia });
      toast.success('Entrega confirmada! Aguardando conferência do responsável.');
      setConfirmOpen(false); refresh();
    } catch (e) { toast.error(errMsg(e, 'Não foi possível confirmar a entrega')); }
    finally { setConfirming(false); }
  };

  const remover = async () => {
    try {
      await api.delete(`/guias/${removeGuia.id}`, { data: { motivo } });
      toast.success('Guia removida (registrado na auditoria)');
      setRemoveGuia(null); setMotivo(''); refresh();
    } catch (e) { toast.error(errMsg(e, 'Erro ao remover guia')); }
  };

  if (loading && !data) return <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>;
  if (!data) return null;

  const p = data.profissional;
  const podeEditar = data.pode_editar && data.pode_lancar && can('lancar_guias');
  const podeRemover = podeEditar && can('excluir_guias');
  const podeConfirmar = podeEditar && p.pode_confirmar_entrega && can('confirmar_entrega_guias');

  return (
    <div className="space-y-4" data-testid="profissional-panel">
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="btn-voltar-lista"><ArrowLeft className="h-4 w-4 mr-1" />Voltar</Button>
        <div className="flex-1 min-w-[200px]">
          <h2 className="text-xl font-bold leading-tight" data-testid="prof-nome">{p.nome}</h2>
          <p className="text-sm text-muted-foreground">{p.categoria_label}{p.subtitulo ? ` · ${p.subtitulo}` : ''} · Competência {fmtDate(competencia)}</p>
        </div>
        <StatusBadge status={p.status} testId="prof-status" />
        <Button variant="outline" size="icon" onClick={load} title="Atualizar"><RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /></Button>
      </div>

      {data.multi ? (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="stats-especialidade">
          <Stat label="Pacientes vinculados" value={p.pacientes_total} />
          <Stat label="Guias esperadas" value={p.esperadas_definida ? p.esperadas : 'Não definida'} cls={p.esperadas_definida ? '' : 'text-base text-muted-foreground'} />
          <Stat label="Guias enviadas" value={p.validas} cls={p.esperadas_definida && p.validas >= p.esperadas ? 'text-green-600' : ''} />
          <Stat label="Pacientes sem guia" value={p.pacientes_sem_guia} cls={p.pacientes_sem_guia > 0 ? 'text-amber-600' : 'text-green-600'} />
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-3">
          <Stat label="Pacientes da competência" value={p.esperadas} />
          <Stat label="Guias lançadas" value={`${p.validas}/${p.esperadas}`} cls={p.validas === p.esperadas && p.esperadas > 0 ? 'text-green-600' : ''} />
          <Stat label="Pendentes" value={p.pendentes} cls={p.pendentes > 0 ? 'text-red-600' : 'text-green-600'} />
        </div>
      )}
      {data.multi && !p.esperadas_definida && (
        <p className="text-xs text-muted-foreground" data-testid="info-esperadas-nao-definida">Guias esperadas não definidas: nenhuma sessão registrada no módulo Especialidades para esta competência. Envie uma guia por atendimento realizado.</p>
      )}

      {data.em_divergencia && data.total_divergencias > 0 && (
        <Card className="border-orange-300 bg-orange-50 dark:bg-orange-950/20">
          <CardContent className="p-3 text-sm flex items-start gap-2" data-testid="alerta-divergencia-prof">
            <AlertTriangle className="h-5 w-5 text-orange-600 shrink-0 mt-0.5" />
            <div><b>{data.total_divergencias} guia(s) com divergência apontada pelo recebimento.</b> Use <b>Substituir guia</b> na linha do paciente para reenviar a guia corrigida à conferência.</div>
          </CardContent>
        </Card>
      )}
      {data.guias_inconsistentes?.length > 0 && (
        <Card className="border-orange-300 bg-orange-50 dark:bg-orange-950/20">
          <CardContent className="p-3 text-sm flex items-start gap-2">
            <AlertTriangle className="h-5 w-5 text-orange-600 shrink-0 mt-0.5" />
            <div>
              <b>{data.guias_inconsistentes.length} guia(s) de pacientes que não pertenciam à carteira deste profissional nesta competência.</b> Remova-as para liberar a confirmação.
              <ul className="mt-1 space-y-1">
                {data.guias_inconsistentes.map((g) => (
                  <li key={g.id} className="flex items-center gap-2">
                    <span>{g.paciente_nome}</span>
                    {podeRemover && <Button size="sm" variant="outline" className="h-6 text-xs" onClick={() => setRemoveGuia(g)} data-testid={`btn-remover-inconsistente-${g.id}`}><Trash2 className="h-3 w-3 mr-1" />Remover</Button>}
                  </li>
                ))}
              </ul>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-0 divide-y">
          {data.pacientes.length === 0 && <p className="p-6 text-center text-sm text-muted-foreground">Nenhum paciente ativo no Kanban para este profissional.</p>}
          {data.pacientes.map((pac) => (
            <div key={pac.paciente_id} className="flex flex-wrap items-center gap-2 p-3" data-testid={`paciente-row-${pac.paciente_id}`}>
              <span className="w-7 text-xs text-muted-foreground">{String(pac.numero).padStart(2, '0')}</span>
              <div className="flex-1 min-w-[160px]">
                <p className="font-medium text-sm">{pac.paciente_nome}{pac.numero_prontuario && <span className="ml-1 font-mono text-[10px] text-muted-foreground" title="Nº prontuário">#{pac.numero_prontuario}</span>}</p>
                <p className="text-xs text-muted-foreground" title={pac.vinculo_desde ? `Vinculado ao profissional desde ${fmtDate(pac.vinculo_desde)}` : ''} data-testid={`vinculo-desde-${pac.paciente_id}`}>{pac.vinculo_desde ? `Desde ${fmtDate(pac.vinculo_desde)}` : 'Ativo'}</p>
                {pac.divergencias?.length > 0 && pac.divergencias.map((d, i) => (
                  <p key={i} className="text-xs text-orange-700 flex items-start gap-1 mt-0.5" data-testid={`divergencia-motivo-${pac.paciente_id}`}><AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />{TIPO_DIVERGENCIA_LABELS[d.tipo] || d.tipo}{d.descricao ? ` — ${d.descricao}` : ''}</p>
                ))}
              </div>
              {data.multi ? (
                <div className="w-full sm:w-auto sm:flex-[2] min-w-[240px] space-y-1" data-testid={`guias-pac-${pac.paciente_id}`}>
                  <p className="text-xs text-muted-foreground">{pac.total_guias} guia(s) enviada(s){pac.sessoes_registradas ? ` · ${pac.sessoes_registradas} sessão(ões) registrada(s)` : ''}</p>
                  {pac.guias.map((g) => {
                    const v = VALIDACAO_STATUS[g.validacao] || VALIDACAO_STATUS.pendente;
                    return (
                      <div key={g.id} className="flex flex-wrap items-center gap-1.5 text-xs rounded border px-2 py-1" data-testid={`guia-item-${g.id}`}>
                        <span className="font-medium">{fmtDate(g.data_atendimento)}</span>
                        <Badge variant="outline" className="text-[10px]">{g.tipo_guia_label}</Badge>
                        <span className="text-muted-foreground truncate max-w-[140px]">{g.tipo_documento_label}</span>
                        {p.status !== 'pendente' && p.status !== 'pronto_para_entrega' && <Badge className={`text-[10px] ${v.cls}`} data-testid={`validacao-${g.id}`}>{v.label}</Badge>}
                        {g.validacao === 'devolvida' && g.motivo_devolucao && <span className="text-red-700 w-full">Motivo: {g.motivo_devolucao}</span>}
                        <span className="ml-auto flex gap-1">
                          <Button size="sm" variant="ghost" className="h-6 px-1.5" onClick={() => setViewGuia(g.id)} data-testid={`btn-ver-guia-${g.id}`}><Eye className="h-3.5 w-3.5" /></Button>
                          {(podeEditar || (data.pode_lancar && (g.validacao === 'devolvida' || g.status === 'divergencia'))) && <Button size="sm" variant="ghost" className="h-6 px-1.5" title="Substituir" onClick={() => { setGuiaAlvo(g); setUploadPac(pac); }} data-testid={`btn-substituir-guia-${g.id}`}><RefreshCw className="h-3.5 w-3.5" /></Button>}
                          {podeRemover && <Button size="sm" variant="ghost" className="h-6 px-1.5 text-red-600" title="Remover" onClick={() => setRemoveGuia(g)} data-testid={`btn-remover-guia-${g.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ) : <GuiaStatusBadge status={pac.guia_status} />}
              <div className="flex gap-1.5">
                {!data.multi && pac.guia && <Button size="sm" variant="outline" onClick={() => setViewGuia(pac.guia.id)} data-testid={`btn-ver-guia-${pac.paciente_id}`}><Eye className="h-4 w-4 sm:mr-1" /><span className="hidden sm:inline">Visualizar</span></Button>}
                {!data.multi && !podeEditar && pac.pode_substituir && data.pode_lancar && (
                  <Button size="sm" className="bg-orange-600 hover:bg-orange-700 text-white" onClick={() => setUploadPac(pac)} data-testid={`btn-substituir-divergencia-${pac.paciente_id}`}>
                    <RefreshCw className="h-4 w-4 sm:mr-1" /><span className="hidden sm:inline">Substituir guia</span>
                  </Button>
                )}
                {podeEditar && (
                  <Button size="sm" className={pac.guia && !data.multi ? '' : 'bg-primary'} variant={pac.guia && !data.multi ? 'secondary' : 'default'} onClick={() => { setGuiaAlvo(null); setUploadPac(pac); }} data-testid={`btn-anexar-guia-${pac.paciente_id}`}>
                    {pac.guia && !data.multi ? <RefreshCw className="h-4 w-4 sm:mr-1" /> : <Paperclip className="h-4 w-4 sm:mr-1" />}
                    <span className="hidden sm:inline">{data.multi ? 'Enviar guia' : pac.guia ? 'Substituir' : 'Anexar guia'}</span>
                  </Button>
                )}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {data.pode_lancar && can('confirmar_entrega_guias') && p.status !== 'sem_pacientes' && (
        <div className="sticky bottom-3 z-10">
          {data.pode_editar ? (
            <Button className={`w-full h-12 text-base ${podeConfirmar ? 'bg-green-600 hover:bg-green-700' : ''}`} disabled={!podeConfirmar} onClick={() => setConfirmOpen(true)} data-testid="btn-confirmar-entrega">
              {podeConfirmar ? <CheckCircle2 className="h-5 w-5 mr-2" /> : <Lock className="h-5 w-5 mr-2" />}
              {podeConfirmar ? 'CONFIRMAR ENTREGA DAS GUIAS' : data.multi ? (p.validas === 0 ? 'CONFIRMAR ENTREGA (nenhuma guia enviada)' : 'CONFIRMAR ENTREGA (guias inconsistentes/devolvidas)') : `CONFIRMAR ENTREGA (${p.pendentes} pendente${p.pendentes !== 1 ? 's' : ''})`}
            </Button>
          ) : (
            <div className="rounded-lg border bg-card p-3 text-sm flex items-center gap-2" data-testid="entrega-status-info">
              {p.status === 'recebido' ? <CheckCircle2 className="h-5 w-5 text-emerald-600" /> : <FileText className="h-5 w-5 text-blue-600" />}
              <span>
                {p.status === 'recebido' ? <>Recebimento concluído — <b>{p.numero_recebimento}</b></> : p.status === 'com_divergencia' ? 'Entrega com divergência. Substitua as guias apontadas para que o recebimento possa prosseguir.' : 'Entrega confirmada. Aguardando conferência do responsável pelo recebimento.'}
              </span>
            </div>
          )}
        </div>
      )}

      <UploadGuiaDialog open={!!uploadPac} onClose={() => { setUploadPac(null); setGuiaAlvo(null); }} paciente={uploadPac} profKey={profKey} competencia={competencia} onSaved={refresh}
        multi={data.multi} tiposGuia={data.tipos_guia} tiposDocumento={data.tipos_documento} guiaAlvo={guiaAlvo} />
      <GuiaViewer guiaId={viewGuia} open={!!viewGuia} onClose={() => setViewGuia(null)} />

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="confirmar-entrega-dialog">
          <DialogHeader>
            <DialogTitle>Confirmar entrega das guias</DialogTitle>
            <DialogDescription>Você confirma que todas as guias dos pacientes ativos listados foram anexadas e estão sendo entregues para conferência?</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div className="rounded border p-2 flex items-center gap-2"><Users className="h-4 w-4" /> {p.pacientes_total} pacientes</div>
            <div className="rounded border p-2 flex items-center gap-2"><FileText className="h-4 w-4" /> {p.lancadas} guias</div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>Cancelar</Button>
            <Button className="bg-green-600 hover:bg-green-700" onClick={confirmarEntrega} disabled={confirming} data-testid="btn-confirmar-entrega-final">
              {confirming && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Confirmar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!removeGuia} onOpenChange={(v) => !v && setRemoveGuia(null)}>
        <DialogContent data-testid="remover-guia-dialog">
          <DialogHeader>
            <DialogTitle>Remover guia de {removeGuia?.paciente_nome}</DialogTitle>
            <DialogDescription>A remoção fica registrada na auditoria. Informe o motivo.</DialogDescription>
          </DialogHeader>
          <Textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Motivo da remoção" data-testid="input-motivo-remocao" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRemoveGuia(null)}>Cancelar</Button>
            <Button variant="destructive" disabled={!motivo.trim()} onClick={remover} data-testid="btn-confirmar-remocao">Remover</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
